import React, { useState } from 'react';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/base';
import { Modal } from '@/components/base';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/base/Form/Select';
import { appointmentsApi } from '@/lib/api/appointments';
import { medplumResourcesApi } from '@/lib/api/medplum-resources';
import { vitalsApi } from '@/lib/api/vitals';
import { useAuthStore } from '@/lib/stores/authStore';
import { toast } from 'sonner';
import { createComponentLogger } from '@/lib/utils/logger';
import {
  getCurrentTimeString,
  getEndOfDayTimeString,
  createDateFromDateAndTime,
  getTodayDateString,
} from '@/lib/utils/date';
import { ChevronDown, ChevronUp, Activity } from 'lucide-react';
import type { VitalSigns } from '@/lib/types/consultation';

const logger = createComponentLogger('ScheduleAppointmentModal');

// Zod schema for vitals validation
const vitalsSchema = z.object({
  bloodPressure: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val.trim() === '') return true; // Empty is valid
        return /^\d{2,3}\/\d{2,3}$/.test(val.trim());
      },
      { message: 'Format: Systolic/Diastolic (e.g., 120/80)' }
    )
    .refine(
      (val) => {
        if (!val || val.trim() === '') return true;
        const match = val.trim().match(/^(\d+)\/(\d+)$/);
        if (!match) return false;
        const systolic = parseInt(match[1], 10);
        const diastolic = parseInt(match[2], 10);
        return (
          systolic > diastolic &&
          systolic >= 50 &&
          systolic <= 250 &&
          diastolic >= 30 &&
          diastolic <= 180
        );
      },
      {
        message: 'Systolic: 50–250, diastolic: 30–180, systolic must exceed diastolic',
      }
    ),
  heartRate: z
    .number()
    .optional()
    .refine((val) => val === undefined || (val >= 30 && val <= 220), {
      message: 'Heart rate must be between 30 and 220 bpm',
    }),
  temperature: z
    .number()
    .optional()
    .refine((val) => val === undefined || (val >= 90 && val <= 110), {
      message: 'Temperature must be between 90°F and 110°F',
    }),
  respiratoryRate: z
    .number()
    .optional()
    .refine((val) => val === undefined || (val >= 5 && val <= 60), {
      message: 'Respiratory rate must be between 5 and 60 breaths/min',
    }),
  oxygenSaturation: z
    .number()
    .optional()
    .refine((val) => val === undefined || (val >= 50 && val <= 100), {
      message: 'SpO2 must be between 50% and 100%',
    }),
  bloodSugar: z
    .number()
    .optional()
    .refine((val) => val === undefined || (val >= 20 && val <= 700), {
      message: 'Blood sugar must be between 20 and 700 mg/dL',
    }),
  weight: z
    .number()
    .optional()
    .refine((val) => val === undefined || (val >= 1 && val <= 400), {
      message: 'Weight must be between 1 and 400 kg',
    }),
  height: z
    .number()
    .optional()
    .refine((val) => val === undefined || (val >= 30 && val <= 300), {
      message: 'Height must be between 30 and 300 cm',
    }),
});

// Visit type options for the dropdown
const VISIT_TYPES = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'follow-up', label: 'Follow Up' },
  { value: 'routine-checkup', label: 'Routine Checkup' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'vaccination', label: 'Vaccination' },
  { value: 'lab-test', label: 'Lab Test' },
] as const;

// Initial empty vitals state
const EMPTY_VITALS: VitalSigns = {
  bloodPressure: '',
  heartRate: undefined,
  temperature: undefined,
  respiratoryRate: undefined,
  oxygenSaturation: undefined,
  bloodSugar: undefined,
  weight: undefined,
  height: undefined,
};

export interface PatientData {
  uuid: string;
  name: string;
  age: number;
  gender: 'M' | 'F' | 'O' | 'U';
  phoneNumber?: string | null;
  /** Secondary identifier value (e.g. book number / OPD number) */
  identifier?: string | null;
}

export interface ScheduleAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  patient: PatientData | null;
  onAppointmentCreated?: () => void;
}

export const ScheduleAppointmentModal: React.FC<ScheduleAppointmentModalProps> = ({
  isOpen,
  onClose,
  patient,
  onAppointmentCreated,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [visitType, setVisitType] = useState('consultation');
  const [appointmentDate, setAppointmentDate] = useState(getTodayDateString);
  const [startTime, setStartTime] = useState(getCurrentTimeString);
  const [endTime, setEndTime] = useState(getEndOfDayTimeString);

  // Get current user for default practitioner selection
  const currentUser = useAuthStore((state) => state.user);

  // Selected practitioner state
  const [selectedPractitionerId, setSelectedPractitionerId] = useState<string>('');

  // Fetch practitioners list
  const { data: practitionersData, isLoading: isLoadingPractitioners } = useQuery({
    queryKey: ['practitioners-for-appointment'],
    queryFn: async () => {
      return medplumResourcesApi.listPractitioners({ count: 100 });
    },
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  // Set default practitioner when practitioners load or when modal opens
  React.useEffect(() => {
    const practitioners = practitionersData?.practitioners;
    if (practitioners && practitioners.length > 0 && !selectedPractitionerId) {
      const normalize = (s?: string) => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
      const fullName = (p: any) => {
        const n = p.name?.[0];
        return normalize(`${n?.given?.join(' ') || ''} ${n?.family || ''}`);
      };
      // Match the logged-in practitioner by id; fall back to their display name
      const loginName = normalize(
        [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ') ||
          currentUser?.username
      );
      const currentUserInList =
        practitioners.find((p: any) => p.id === currentUser?.id) ??
        (currentUser?.role === 'practitioner' && loginName
          ? practitioners.find((p: any) => fullName(p) === loginName)
          : undefined);
      if (currentUserInList) {
        setSelectedPractitionerId(currentUserInList.id);
      } else {
        setSelectedPractitionerId(practitioners[0]?.id || '');
      }
    }
  }, [practitionersData, currentUser, selectedPractitionerId, isOpen]);

  // Reset selected practitioner when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setSelectedPractitionerId('');
    }
  }, [isOpen]);

  // Vitals state
  const [showVitals, setShowVitals] = useState(false);
  const [vitals, setVitals] = useState<VitalSigns>({ ...EMPTY_VITALS });
  const [vitalsErrors, setVitalsErrors] = useState<Record<string, string>>({});

  // Check if any vitals are filled
  const hasVitals = () => {
    return (
      (vitals.bloodPressure && vitals.bloodPressure.trim() !== '') ||
      (vitals.heartRate !== undefined && !isNaN(vitals.heartRate)) ||
      (vitals.temperature !== undefined && !isNaN(vitals.temperature)) ||
      (vitals.respiratoryRate !== undefined && !isNaN(vitals.respiratoryRate)) ||
      (vitals.oxygenSaturation !== undefined && !isNaN(vitals.oxygenSaturation)) ||
      (vitals.bloodSugar !== undefined && !isNaN(vitals.bloodSugar)) ||
      (vitals.weight !== undefined && !isNaN(vitals.weight)) ||
      (vitals.height !== undefined && !isNaN(vitals.height))
    );
  };

  // Validate vitals using Zod schema - returns true if valid, false otherwise
  const validateVitals = (): boolean => {
    // If no vitals are filled, skip validation
    if (!hasVitals()) {
      setVitalsErrors({});
      return true;
    }

    const result = vitalsSchema.safeParse(vitals);
    if (!result.success) {
      const errors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as string;
        errors[field] = err.message;
      });
      setVitalsErrors(errors);
      // Show first error as toast
      const firstError = result.error.errors[0];
      toast.error(firstError.message);
      return false;
    }
    setVitalsErrors({});
    return true;
  };

  // Handle vitals field change
  const handleVitalChange = (field: keyof VitalSigns, value: string) => {
    // Clear error for this field when user starts typing
    setVitalsErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });

    if (field === 'bloodPressure') {
      setVitals((prev) => ({ ...prev, [field]: value }));
    } else {
      const numValue = value === '' ? undefined : parseFloat(value);
      setVitals((prev) => ({ ...prev, [field]: numValue }));
    }
  };

  // Validate a single vitals field on blur for immediate feedback
  const handleVitalBlur = (field: keyof VitalSigns) => {
    const value = vitals[field];
    // Skip if empty/undefined — field is optional
    if (value === undefined || value === '' || (typeof value === 'number' && isNaN(value))) return;
    const partial: Partial<VitalSigns> = { [field]: value };
    const result = vitalsSchema.safeParse({ ...EMPTY_VITALS, ...partial });
    if (!result.success) {
      const fieldError = result.error.errors.find((e) => e.path[0] === field);
      if (fieldError) {
        setVitalsErrors((prev) => ({ ...prev, [field]: fieldError.message }));
      }
    }
  };

  // Prevent scientific notation characters (e/E/+/-) in numeric vitals inputs.
  // HTML type="number" natively allows these keystrokes but they are not valid for clinical data.
  const blockInvalidNumericKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
  };

  // Get the visit type label for display in appointment
  const getVisitTypeLabel = (value: string) => {
    const type = VISIT_TYPES.find((t) => t.value === value);
    return type?.label || 'Follow Up';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!patient) {
      toast.error('Patient information is missing');
      return;
    }

    // Validate date is not in the past
    const selectedDate = new Date(appointmentDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      toast.error('Cannot schedule appointment for a past date');
      return;
    }

    // Validate vitals using Zod before submission
    if (!validateVitals()) {
      // Error toast is shown in validateVitals()
      return;
    }

    setIsLoading(true);
    try {
      // Get logged-in user
      const user = useAuthStore.getState().user;
      if (!user) {
        toast.error('User not authenticated');
        return;
      }

      // Determine practitioner ID: use selected if available, otherwise current user
      const practitionerId = selectedPractitionerId || user.id;

      // Validate practitioner selection
      if (!practitionerId) {
        toast.error('Please select a practitioner for the appointment');
        setIsLoading(false);
        return;
      }

      const visitTypeLabel = getVisitTypeLabel(visitType);

      // Use selected date with selected start time (ISO 8601 format to avoid timezone issues)
      const appointmentStartDate = createDateFromDateAndTime(appointmentDate, startTime);

      // Use selected date with selected end time
      const appointmentEndDate = createDateFromDateAndTime(appointmentDate, endTime);

      let encounterId: string | undefined;

      // If vitals are provided, create Encounter first to link vitals
      if (hasVitals()) {
        logger.log('Creating Encounter for appointment with vitals');

        // Create Encounter
        const encounter = await medplumResourcesApi.createEncounter({
          patientId: `Patient/${patient.uuid}`,
          practitionerId: `Practitioner/${practitionerId}`,
          status: 'planned',
          class: 'outpatient',
          type: visitTypeLabel,
          period: {
            start: appointmentStartDate.toISOString(),
          },
        });

        encounterId = encounter.id;
        logger.log('Encounter created:', encounterId);

        // Save vitals linked to the encounter (only if encounterId is valid)
        if (encounterId) {
          await vitalsApi.saveVitals({
            patientId: patient.uuid,
            encounterId,
            vitals,
            practitionerId,
          });
          logger.log('Vitals saved for encounter:', encounterId);
        }
      }

      logger.log('Creating appointment for patient:', patient.uuid);
      const appointment = await appointmentsApi.createAppointment({
        status: 'booked',
        start: appointmentStartDate.toISOString(),
        end: appointmentEndDate.toISOString(),
        patientId: patient.uuid,
        practitionerId: practitionerId,
        appointmentType: visitTypeLabel,
        description: `${visitTypeLabel} appointment for ${patient.name}`,
        encounterId: encounterId, // Link to encounter if created
      });
      logger.log('Appointment created:', appointment.id);

      // If encounter was created, update it to link back to appointment
      if (encounterId) {
        try {
          await medplumResourcesApi.updateEncounter(encounterId, {
            appointment: [{ reference: `Appointment/${appointment.id}` }],
          });
          logger.log('Encounter linked to appointment:', appointment.id);
        } catch (linkError) {
          logger.error('Failed to link encounter to appointment:', linkError);
          // Don't fail - appointment and vitals are already created
        }
      }

      toast.success('Appointment created successfully', {
        description: hasVitals()
          ? `${visitTypeLabel} for ${patient.name} with vitals recorded`
          : `${visitTypeLabel} for ${patient.name}`,
      });
      onAppointmentCreated?.();
      handleClose();
    } catch (error) {
      logger.error('Failed to create appointment:', error);
      toast.error('Failed to create appointment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setVisitType('consultation');
      setAppointmentDate(new Date().toISOString().split('T')[0]);
      setStartTime(getCurrentTimeString());
      setEndTime(getEndOfDayTimeString());
      setShowVitals(false);
      setVitals({ ...EMPTY_VITALS });
      setVitalsErrors({});
      setSelectedPractitionerId('');
      onClose();
    }
  };

  if (!patient) return null;

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && handleClose()}
      size="md"
      disableOutsideClick={true}
      footer={
        <Button
          type="submit"
          variant="primary"
          disabled={isLoading}
          loading={isLoading}
          className="w-full rounded-lg bg-[#061a61] py-2.5 text-sm font-medium hover:bg-[#061a61]/90"
          onClick={handleSubmit}
        >
          {isLoading ? 'Creating...' : 'Create Appointment'}
        </Button>
      }
    >
      <div
        className="scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400 max-h-[65vh] overflow-y-auto pr-2"
        style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db #f3f4f6' }}
      >
        {/* Centered Header */}
        <div className="mb-4 text-center">
          <h2 className="font-hanken text-xl font-medium leading-overview-title tracking-normal text-gray-700">
            New Appointment
          </h2>
          <p className="mx-auto mt-2 w-3/4 font-hanken text-base font-normal leading-label-2 tracking-label-2 text-gray-700">
            Create a new appointment for this patient
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Patient's name / Phone Number - Read only display */}
          <div>
            <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
              Patient
            </label>
            <div className="flex h-10 w-full items-center rounded-lg bg-[#fbfafd] px-3 ring-1 ring-inset ring-gray-200">
              <span className="text-sm text-gray-900">
                {patient.name}
                {patient.phoneNumber && ` • ${patient.phoneNumber}`}
              </span>
            </div>
          </div>

          {/* Practitioner Dropdown */}
          <div>
            <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
              Assign to Practitioner <span className="text-red-500">*</span>
            </label>
            <Select
              value={selectedPractitionerId}
              onValueChange={setSelectedPractitionerId}
              disabled={isLoading || isLoadingPractitioners}
            >
              <SelectTrigger className="h-10 w-full rounded-lg border-0 bg-[#fbfafd] px-3 text-sm text-gray-900 ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-[#061a61]">
                <SelectValue
                  placeholder={
                    isLoadingPractitioners ? 'Loading practitioners...' : 'Select a practitioner'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {practitionersData?.practitioners?.map((practitioner: any) => {
                  const name = practitioner.name?.[0];
                  const displayName = name
                    ? `${name.prefix?.[0] ? name.prefix[0] + ' ' : ''}${name.given?.join(' ') || ''} ${name.family || ''}`.trim()
                    : practitioner.id || 'Unknown';
                  return (
                    <SelectItem key={practitioner.id} value={practitioner.id || ''}>
                      {displayName}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Visit Type Dropdown */}
          <div>
            <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
              Visit Type
            </label>
            <select
              value={visitType}
              onChange={(e) => setVisitType(e.target.value)}
              disabled={isLoading}
              className="flex h-10 w-full appearance-none items-center rounded-lg border-0 bg-[#fbfafd] px-3 text-sm text-gray-900 outline-none ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-[#061a61]"
            >
              {VISIT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Date Field */}
          <div>
            <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
              Date
            </label>
            <input
              type="date"
              value={appointmentDate}
              onChange={(e) => setAppointmentDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              disabled={isLoading}
              className="flex h-10 w-full items-center rounded-lg border-0 bg-[#fbfafd] px-3 text-sm text-gray-900 outline-none ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-[#061a61]"
            />
          </div>

          {/* Time Fields - Start and End */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
                Start Time
              </label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                disabled={isLoading}
                className="flex h-10 w-full items-center rounded-lg border-0 bg-[#fbfafd] px-3 text-sm text-gray-900 outline-none ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-[#061a61]"
              />
            </div>
            <div>
              <label className="mb-1.5 block font-hanken text-sm font-medium text-gray-700">
                End Time
              </label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                disabled={isLoading}
                className="flex h-10 w-full items-center rounded-lg border-0 bg-[#fbfafd] px-3 text-sm text-gray-900 outline-none ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-[#061a61]"
              />
            </div>
          </div>

          {/* Vitals Section - Collapsible (hidden for front desk role) */}
          {currentUser?.role !== 'frontdesk' && (
            <div className="border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={() => setShowVitals(!showVitals)}
                className="flex w-full items-center justify-between rounded-lg bg-[#f8f9fc] px-4 py-3 text-left transition-colors hover:bg-[#f0f2f8]"
                disabled={isLoading}
              >
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-[#061a61]" />
                  <span className="font-hanken text-sm font-medium text-gray-700">
                    Record Vitals
                  </span>
                  {hasVitals() && (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                      Recorded
                    </span>
                  )}
                </div>
                {showVitals ? (
                  <ChevronUp className="h-4 w-4 text-gray-700" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-700" />
                )}
              </button>

              {showVitals && (
                <div className="mt-3 space-y-3 rounded-lg border border-gray-200 bg-white p-3">
                  <p className="text-xs text-gray-500">
                    All vitals are optional. Fill in the available measurements.
                  </p>

                  {/* Row 1: Blood Pressure, Heart Rate, Temperature, Respiratory Rate */}
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <label
                        className={`mb-1 block font-hanken text-xs font-medium ${vitalsErrors.bloodPressure ? 'text-red-500' : 'text-gray-700'}`}
                      >
                        BP (mmHg)
                      </label>
                      <input
                        type="text"
                        placeholder="120/80"
                        value={vitals.bloodPressure || ''}
                        onChange={(e) => handleVitalChange('bloodPressure', e.target.value)}
                        onBlur={() => handleVitalBlur('bloodPressure')}
                        pattern="\d{2,3}/\d{2,3}"
                        title="Enter blood pressure as Systolic/Diastolic, e.g., 120/80"
                        disabled={isLoading}
                        className={`flex h-8 w-full items-center rounded-md border-0 bg-[#fbfafd] px-2 text-sm text-gray-900 outline-none ring-1 ring-inset focus:ring-2 ${vitalsErrors.bloodPressure ? 'ring-red-500 focus:ring-red-500' : 'ring-gray-200 focus:ring-[#061a61]'}`}
                      />
                      {vitalsErrors.bloodPressure && (
                        <p className="mt-0.5 text-xs text-red-500">{vitalsErrors.bloodPressure}</p>
                      )}
                    </div>
                    <div>
                      <label
                        className={`mb-1 block font-hanken text-xs font-medium ${vitalsErrors.heartRate ? 'text-red-500' : 'text-gray-700'}`}
                      >
                        HR (bpm)
                      </label>
                      <input
                        type="number"
                        placeholder="72"
                        min={30}
                        max={220}
                        value={vitals.heartRate ?? ''}
                        onChange={(e) => handleVitalChange('heartRate', e.target.value)}
                        onKeyDown={blockInvalidNumericKey}
                        onBlur={() => handleVitalBlur('heartRate')}
                        disabled={isLoading}
                        className={`flex h-8 w-full items-center rounded-md border-0 bg-[#fbfafd] px-2 text-sm text-gray-900 outline-none ring-1 ring-inset focus:ring-2 ${vitalsErrors.heartRate ? 'ring-red-500 focus:ring-red-500' : 'ring-gray-200 focus:ring-[#061a61]'}`}
                      />
                      {vitalsErrors.heartRate && (
                        <p className="mt-0.5 text-xs text-red-500">{vitalsErrors.heartRate}</p>
                      )}
                    </div>
                    <div>
                      <label
                        className={`mb-1 block font-hanken text-xs font-medium ${vitalsErrors.temperature ? 'text-red-500' : 'text-gray-700'}`}
                      >
                        Temp (°F)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        placeholder="98.6"
                        min={90}
                        max={110}
                        value={vitals.temperature ?? ''}
                        onChange={(e) => handleVitalChange('temperature', e.target.value)}
                        onKeyDown={blockInvalidNumericKey}
                        onBlur={() => handleVitalBlur('temperature')}
                        disabled={isLoading}
                        className={`flex h-8 w-full items-center rounded-md border-0 bg-[#fbfafd] px-2 text-sm text-gray-900 outline-none ring-1 ring-inset focus:ring-2 ${vitalsErrors.temperature ? 'ring-red-500 focus:ring-red-500' : 'ring-gray-200 focus:ring-[#061a61]'}`}
                      />
                      {vitalsErrors.temperature && (
                        <p className="mt-0.5 text-xs text-red-500">{vitalsErrors.temperature}</p>
                      )}
                    </div>
                    <div>
                      <label
                        className={`mb-1 block font-hanken text-xs font-medium ${vitalsErrors.respiratoryRate ? 'text-red-500' : 'text-gray-700'}`}
                      >
                        RR (/min)
                      </label>
                      <input
                        type="number"
                        placeholder="16"
                        min={5}
                        max={60}
                        value={vitals.respiratoryRate ?? ''}
                        onChange={(e) => handleVitalChange('respiratoryRate', e.target.value)}
                        onKeyDown={blockInvalidNumericKey}
                        onBlur={() => handleVitalBlur('respiratoryRate')}
                        disabled={isLoading}
                        className={`flex h-8 w-full items-center rounded-md border-0 bg-[#fbfafd] px-2 text-sm text-gray-900 outline-none ring-1 ring-inset focus:ring-2 ${vitalsErrors.respiratoryRate ? 'ring-red-500 focus:ring-red-500' : 'ring-gray-200 focus:ring-[#061a61]'}`}
                      />
                      {vitalsErrors.respiratoryRate && (
                        <p className="mt-0.5 text-xs text-red-500">
                          {vitalsErrors.respiratoryRate}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Row 2: SpO2, Blood Sugar, Weight, Height */}
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <label
                        className={`mb-1 block font-hanken text-xs font-medium ${vitalsErrors.oxygenSaturation ? 'text-red-500' : 'text-gray-700'}`}
                      >
                        SpO2 (%)
                      </label>
                      <input
                        type="number"
                        placeholder="98"
                        min={50}
                        max={100}
                        value={vitals.oxygenSaturation ?? ''}
                        onChange={(e) => handleVitalChange('oxygenSaturation', e.target.value)}
                        onKeyDown={blockInvalidNumericKey}
                        onBlur={() => handleVitalBlur('oxygenSaturation')}
                        disabled={isLoading}
                        className={`flex h-8 w-full items-center rounded-md border-0 bg-[#fbfafd] px-2 text-sm text-gray-900 outline-none ring-1 ring-inset focus:ring-2 ${vitalsErrors.oxygenSaturation ? 'ring-red-500 focus:ring-red-500' : 'ring-gray-200 focus:ring-[#061a61]'}`}
                      />
                      {vitalsErrors.oxygenSaturation && (
                        <p className="mt-0.5 text-xs text-red-500">
                          {vitalsErrors.oxygenSaturation}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        className={`mb-1 block font-hanken text-xs font-medium ${vitalsErrors.bloodSugar ? 'text-red-500' : 'text-gray-700'}`}
                      >
                        Sugar (mg/dL)
                      </label>
                      <input
                        type="number"
                        placeholder="100"
                        min={20}
                        max={700}
                        value={vitals.bloodSugar ?? ''}
                        onChange={(e) => handleVitalChange('bloodSugar', e.target.value)}
                        onKeyDown={blockInvalidNumericKey}
                        onBlur={() => handleVitalBlur('bloodSugar')}
                        disabled={isLoading}
                        className={`flex h-8 w-full items-center rounded-md border-0 bg-[#fbfafd] px-2 text-sm text-gray-900 outline-none ring-1 ring-inset focus:ring-2 ${vitalsErrors.bloodSugar ? 'ring-red-500 focus:ring-red-500' : 'ring-gray-200 focus:ring-[#061a61]'}`}
                      />
                      {vitalsErrors.bloodSugar && (
                        <p className="mt-0.5 text-xs text-red-500">{vitalsErrors.bloodSugar}</p>
                      )}
                    </div>
                    <div>
                      <label
                        className={`mb-1 block font-hanken text-xs font-medium ${vitalsErrors.weight ? 'text-red-500' : 'text-gray-700'}`}
                      >
                        Weight (kg)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        placeholder="70"
                        min={1}
                        max={400}
                        value={vitals.weight ?? ''}
                        onChange={(e) => handleVitalChange('weight', e.target.value)}
                        onKeyDown={blockInvalidNumericKey}
                        onBlur={() => handleVitalBlur('weight')}
                        disabled={isLoading}
                        className={`flex h-8 w-full items-center rounded-md border-0 bg-[#fbfafd] px-2 text-sm text-gray-900 outline-none ring-1 ring-inset focus:ring-2 ${vitalsErrors.weight ? 'ring-red-500 focus:ring-red-500' : 'ring-gray-200 focus:ring-[#061a61]'}`}
                      />
                      {vitalsErrors.weight && (
                        <p className="mt-0.5 text-xs text-red-500">{vitalsErrors.weight}</p>
                      )}
                    </div>
                    <div>
                      <label
                        className={`mb-1 block font-hanken text-xs font-medium ${vitalsErrors.height ? 'text-red-500' : 'text-gray-700'}`}
                      >
                        Height (cm)
                      </label>
                      <input
                        type="number"
                        placeholder="175"
                        min={30}
                        max={300}
                        value={vitals.height ?? ''}
                        onChange={(e) => handleVitalChange('height', e.target.value)}
                        onKeyDown={blockInvalidNumericKey}
                        onBlur={() => handleVitalBlur('height')}
                        disabled={isLoading}
                        className={`flex h-8 w-full items-center rounded-md border-0 bg-[#fbfafd] px-2 text-sm text-gray-900 outline-none ring-1 ring-inset focus:ring-2 ${vitalsErrors.height ? 'ring-red-500 focus:ring-red-500' : 'ring-gray-200 focus:ring-[#061a61]'}`}
                      />
                      {vitalsErrors.height && (
                        <p className="mt-0.5 text-xs text-red-500">{vitalsErrors.height}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </form>
      </div>
    </Modal>
  );
};

export default ScheduleAppointmentModal;
